/**
 * Capacity - assigned points against weekly capacity, person by person.
 *
 * ## Every date here comes from `@botguide/shared`
 *
 * Weeks are Mondays in Denver. `new Date().toISOString().slice(0, 10)` is the
 * wrong answer for seven hours every evening, and hand-rolled week arithmetic
 * is wrong twice a year, so `currentWeekStart`, `addDays` and `weekRange` do
 * all of it. Nothing in this file touches a millisecond.
 *
 * ## A contractor may read this screen
 *
 * They see everybody's points, because the team plans together and points are
 * not pay. There is no money on this screen, and `policy.ts` refuses a client
 * the route entirely.
 */
import { useState } from 'react';

import type { PlainDate } from '@botguide/shared';
import { addDays, currentWeekStart, formatWeekLabel, weekRange } from '@botguide/shared';

import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Icon,
  LoadingScreen,
  PageHeader,
  Select,
  Stat,
} from '@/components';
import { CapacityGrid, CapacityLegend, summariseCapacity } from '@/components/admin/CapacityGrid';
import { formatCount, formatPoints } from '@/lib/format';
import { useCapacity } from '@/lib/queries';
import { useSession } from '@/lib/session';

/** `capacityQuerySchema` allows 1-12; these are the windows worth offering. */
const WINDOWS = [2, 4, 6, 8, 12] as const;

export default function Capacity() {
  const { user } = useSession();

  // The current week is read once, on mount. Re-deriving it on every render
  // would silently move the grid under someone who left the tab open overnight.
  const [today] = useState<PlainDate>(() => currentWeekStart());
  const [weekStart, setWeekStart] = useState<PlainDate>(today);
  const [weeks, setWeeks] = useState<number>(4);

  const capacity = useCapacity({ weekStart, weeks });

  const window_ = weekRange(weekStart, weeks);
  const first = window_[0] ?? weekStart;
  const last = window_[window_.length - 1] ?? weekStart;

  const report = capacity.data;
  const summary = report ? summariseCapacity(report) : null;

  return (
    <>
      <PageHeader
        title="Capacity"
        description={
          <>
            Story points committed against what each contractor said a week holds, from{' '}
            <span className="font-medium text-ink">{formatWeekLabel(first)}</span> to{' '}
            <span className="font-medium text-ink">{formatWeekLabel(last)}</span>.
          </>
        }
        toolbar={
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div
                className="flex items-center gap-1 rounded-md border border-line bg-surface p-1"
                role="group"
                aria-label="Week navigation"
              >
                <Button
                  size="sm"
                  variant="ghost"
                  className="px-2"
                  onClick={() => setWeekStart((current) => addDays(current, -7))}
                >
                  <Icon name="chevronLeft" size={15} />
                  <span className="sr-only">Previous week</span>
                </Button>
                <Button
                  size="sm"
                  variant={weekStart === today ? 'secondary' : 'ghost'}
                  disabled={weekStart === today}
                  onClick={() => setWeekStart(today)}
                >
                  Today
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="px-2"
                  onClick={() => setWeekStart((current) => addDays(current, 7))}
                >
                  <Icon name="chevronRight" size={15} />
                  <span className="sr-only">Next week</span>
                </Button>
              </div>

              <Select
                label="Weeks shown"
                options={WINDOWS.map((count) => ({ value: String(count), label: `${count} weeks` }))}
                value={String(weeks)}
                onChange={(event) => setWeeks(Number(event.target.value))}
                wrapperClassName="w-36"
              />
            </div>

            {summary ? (
              <Card padding="md" className="rise">
                <div className="grid grid-cols-2 gap-x-4 gap-y-5 sm:grid-cols-3">
                  <Stat
                    label="Over capacity"
                    value={summary.overPeople}
                    tone={summary.overPeople > 0 ? 'var(--danger)' : undefined}
                    hint={
                      summary.overPeople > 0
                        ? `${formatCount(summary.overCells, 'week')} across the window`
                        : 'Nobody is oversubscribed'
                    }
                  />
                  <Stat
                    label="Unestimated"
                    value={summary.unestimatedTasks}
                    tone={summary.unestimatedTasks > 0 ? 'var(--warn)' : undefined}
                    hint="Assigned tasks carrying no points"
                  />
                  <Stat
                    label="Unassigned"
                    value={summary.unassignedTasks}
                    tone={summary.unassignedTasks > 0 ? 'var(--warn)' : undefined}
                    hint={`${formatPoints(summary.unassignedPoints)} points planned on nobody`}
                  />
                </div>
              </Card>
            ) : null}
          </div>
        }
      />

      {capacity.isPending ? (
        <LoadingScreen label="Working out the weeks" />
      ) : capacity.error ? (
        <ErrorState error={capacity.error} onRetry={() => void capacity.refetch()} />
      ) : !report || report.cells.length === 0 ? (
        <EmptyState
          icon="capacity"
          title="No contractors to plan"
          description="Capacity is built from active contractors and their weekly points. Add someone on the People screen and their weeks appear here."
        />
      ) : (
        <>
          {summary && summary.overPeople > 0 ? (
            <p
              role="status"
              className="mb-4 flex items-start gap-2 rounded-md border border-danger/30 bg-danger-soft px-3.5 py-2.5 text-[0.8125rem] text-danger-ink"
            >
              <Icon name="alert" size={15} className="mt-0.5" />
              <span>
                <strong className="font-semibold">
                  {formatCount(summary.overPeople, 'person', 'people')} over capacity
                </strong>{' '}
                in this window — {formatCount(summary.overCells, 'week')} in total. Move work out, or
                move the week.
              </span>
            </p>
          ) : null}

          <CapacityGrid report={report} thisWeek={today} currentUserId={user?.id ?? null} />
          <CapacityLegend />

          {summary && summary.unestimatedTasks > 0 ? (
            <p className="mt-3 max-w-2xl text-[0.8125rem] text-muted">
              {formatCount(summary.unestimatedTasks, 'assigned task')} in this window{' '}
              {summary.unestimatedTasks === 1 ? 'has' : 'have'} no estimate, so none of that work is
              in the points above. That is how a week gets oversubscribed without the grid ever
              showing it.
            </p>
          ) : null}
        </>
      )}
    </>
  );
}
